import React from 'react';
import { useNotifications } from '../contexts/NotificationContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Bell,
  Check,
  CheckCheck,
  Calendar,
  User,
  MessageSquare,
  Clock,
  Loader2,
} from 'lucide-react';
import { formatDateTime } from '../lib/utils';
import { cn } from '../lib/utils';

const NotificationsPage = () => {
  const { notifications, unreadCount, loading, markAsRead, markAllAsRead } = useNotifications();

  const getNotificationIcon = (type) => {
    if (type.includes('booking')) return <Calendar className="h-5 w-5" />;
    if (type.includes('staff') || type.includes('arrive') || type.includes('depart')) return <Clock className="h-5 w-5" />;
    if (type.includes('chat')) return <MessageSquare className="h-5 w-5" />;
    return <Bell className="h-5 w-5" />;
  };

  const getNotificationColor = (type) => {
    if (type.includes('accepted') || type.includes('completed') || type.includes('depart')) {
      return 'bg-emerald-100 text-emerald-600';
    }
    if (type.includes('declined') || type.includes('cancelled')) {
      return 'bg-red-100 text-red-600';
    }
    if (type.includes('assigned') || type.includes('arrive')) {
      return 'bg-blue-100 text-blue-600';
    }
    return 'bg-zinc-100 text-zinc-600';
  };

  return (
    <div className="space-y-6 pb-20 md:pb-0 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-zinc-900 tracking-tight">Notifications</h1>
          <p className="text-zinc-500 mt-1">
            {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up!'}
          </p>
        </div>
        {unreadCount > 0 && (
          <Button
            variant="outline"
            onClick={markAllAsRead}
            data-testid="mark-all-read-btn"
          >
            <CheckCheck className="mr-2 h-4 w-4" />
            Mark all read
          </Button>
        )}
      </div>

      {/* Notifications List */}
      <Card className="border-zinc-100">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-12">
              <div className="h-16 w-16 rounded-full bg-zinc-100 flex items-center justify-center mx-auto mb-4">
                <Bell className="h-8 w-8 text-zinc-400" />
              </div>
              <h3 className="text-lg font-medium text-zinc-900 mb-1">No notifications</h3>
              <p className="text-zinc-500">You're all caught up!</p>
            </div>
          ) : (
            <div className="divide-y divide-zinc-100">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={cn(
                    'flex gap-4 p-4 transition-colors',
                    !notification.is_read && 'bg-blue-50/50'
                  )}
                  data-testid={`notification-${notification.id}`}
                >
                  <div className={cn(
                    'h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0',
                    getNotificationColor(notification.type)
                  )}>
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className={cn(
                          'font-medium',
                          notification.is_read ? 'text-zinc-700' : 'text-zinc-900'
                        )}>
                          {notification.title}
                        </p>
                        <p className="text-sm text-zinc-500 mt-0.5">
                          {notification.message}
                        </p>
                        <p className="text-xs text-zinc-400 mt-1">
                          {formatDateTime(notification.created_at)}
                        </p>
                      </div>
                      {!notification.is_read && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => markAsRead(notification.id)}
                          className="flex-shrink-0"
                          data-testid={`mark-read-${notification.id}`}
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default NotificationsPage;
